/**
 * PushBroadcastTab — APLEO back-office form to compose and send ad-hoc push
 * notifications to all opted-in devices (iOS + Android).
 *
 * Sections:
 *   1. i18n title/body per locale (11 languages, tabbed to save space)
 *   2. Deep-link target (optional)
 *   3. Targeting (platforms + locales + test-only devices)
 *   4. Preview + dry-run (shows audience count without sending)
 *   5. Send with confirmation
 *   6. Recent broadcasts audit log
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PUSH_TEMPLATES } from "./pushTemplates";

const LOCALES = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "pl", label: "Polski" },
  { code: "ru", label: "Русский" },
  { code: "uk", label: "Українська" },
  { code: "ro", label: "Română" },
  { code: "sv", label: "Svenska" },
  { code: "nb", label: "Norsk" },
];

const emptyPayloads = () =>
  Object.fromEntries(LOCALES.map((l) => [l.code, { title: "", body: "" }]));

export default function PushBroadcastTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [payloads, setPayloads] = useState(emptyPayloads);
  const [activeLocale, setActiveLocale] = useState("fr");
  const [deepLinkPath, setDeepLinkPath] = useState("");
  const [platforms, setPlatforms] = useState({ ios: true, android: true });
  const [targetLocales, setTargetLocales] = useState([]); // empty = all
  const [testIds, setTestIds] = useState("");
  const [dryResult, setDryResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [msg, setMsg] = useState("");

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/admin/push/broadcasts");
      setBroadcasts(r.data.items || []);
    } catch {
      setBroadcasts([]);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const buildBody = useCallback(() => {
    const nonEmpty = {};
    for (const [k, v] of Object.entries(payloads)) {
      if ((v.title || "").trim() || (v.body || "").trim()) {
        nonEmpty[k] = { title: v.title.trim(), body: v.body.trim() };
      }
    }
    const body = {
      payloads: nonEmpty,
      data: deepLinkPath.trim() ? { path: deepLinkPath.trim() } : {},
    };
    const plats = Object.entries(platforms).filter(([, v]) => v).map(([k]) => k);
    if (plats.length && plats.length < 2) body.target_platforms = plats;
    if (targetLocales.length) body.target_locales = targetLocales;
    const ids = testIds
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length) body.test_device_ids = ids;
    return body;
  }, [payloads, deepLinkPath, platforms, targetLocales, testIds]);

  const canSubmit = useMemo(() => {
    return (payloads.fr?.title || "").trim() && (payloads.fr?.body || "").trim();
  }, [payloads]);

  const runDryRun = async () => {
    setMsg("");
    if (!canSubmit) {
      setMsg(isFr ? "Le titre et le corps FR sont obligatoires." : "FR title and body are mandatory.");
      return;
    }
    try {
      const r = await api.post("/admin/push/broadcast", { ...buildBody(), dry_run: true });
      setDryResult(r.data);
    } catch (e) {
      setDryResult(null);
      setMsg(e?.response?.data?.detail || "Erreur dry-run");
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSending(true);
    setMsg("");
    try {
      const r = await api.post("/admin/push/broadcast", buildBody());
      setMsg(
        (isFr ? "✓ Envoyé — id: " : "✓ Sent — id: ") + (r.data.broadcast_id || "n/a") +
        (isFr ? ` (audience: ${r.data.target})` : ` (target: ${r.data.target})`)
      );
      setShowConfirm(false);
      await loadHistory();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Erreur envoi");
    } finally {
      setSending(false);
    }
  };

  const clearForm = () => {
    setPayloads(emptyPayloads());
    setDeepLinkPath("");
    setDryResult(null);
    setMsg("");
  };

  const active = payloads[activeLocale];
  const previewTitle = active?.title || payloads.fr?.title || "PRESENCE";
  const previewBody = active?.body || payloads.fr?.body || "";

  return (
    <div className="space-y-6" data-testid="push-broadcast-tab">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl" style={{ color: "var(--ink)" }}>
            {isFr ? "Notifications push" : "Push notifications"}
          </h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Envoyez un message aux appareils PRESENCE (iOS + Android). Le corps FR sert de repli si une langue est vide."
              : "Send a message to PRESENCE devices (iOS + Android). The FR body is the fallback if a locale is empty."}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost text-sm"
          onClick={clearForm}
          data-testid="push-clear"
        >
          {isFr ? "Effacer" : "Clear"}
        </button>
      </header>

      {/* Templates dropdown — pré-remplit tout le formulaire avec un modèle */}
      <div className="bg-white border p-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <label className="font-body text-xs uppercase tracking-widest" style={{ color: "var(--muted)" }}>
          {isFr ? "Modèle :" : "Template:"}
        </label>
        <select
          data-testid="push-template"
          className="px-3 py-2 border font-body flex-1"
          style={{ borderColor: "var(--border)", background: "#fff", maxWidth: 480 }}
          defaultValue=""
          onChange={(e) => {
            const tpl = PUSH_TEMPLATES.find((t) => t.key === e.target.value);
            if (!tpl) return;
            setPayloads(() => {
              const next = emptyPayloads();
              for (const [loc, v] of Object.entries(tpl.payloads)) {
                if (next[loc]) next[loc] = { title: v.title, body: v.body };
              }
              return next;
            });
            if (tpl.deep_link) setDeepLinkPath(tpl.deep_link);
            setMsg(isFr ? "✓ Modèle chargé — ajustez si besoin puis envoyez." : "✓ Template loaded — adjust as needed then send.");
            e.target.value = "";
          }}
        >
          <option value="">{isFr ? "— Charger un modèle —" : "— Load a template —"}</option>
          {PUSH_TEMPLATES.map((tpl) => (
            <option key={tpl.key} value={tpl.key}>
              {isFr ? tpl.label_fr : tpl.label_en}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FORM — 2/3 width */}
        <div className="lg:col-span-2 space-y-5">
          {/* Locale tabs */}
          <div className="bg-white border p-4" style={{ borderColor: "var(--border)" }}>
            <div className="flex flex-wrap gap-1 mb-4 border-b" style={{ borderColor: "var(--border)" }}>
              {LOCALES.map((l) => {
                const p = payloads[l.code];
                const filled = (p?.title || "").trim() && (p?.body || "").trim();
                return (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`push-locale-${l.code}`}
                    onClick={() => setActiveLocale(l.code)}
                    className="px-3 py-2 font-body text-sm"
                    style={{
                      borderBottom: activeLocale === l.code ? "2px solid var(--cinnabar)" : "2px solid transparent",
                      color: activeLocale === l.code ? "var(--cinnabar)" : "var(--muted)",
                      fontWeight: filled ? 600 : 400,
                    }}
                    title={l.label}
                  >
                    {l.code.toUpperCase()}
                    {filled && <span aria-hidden style={{ marginLeft: 4, color: "var(--gold)" }}>✓</span>}
                    {l.code === "fr" && <span aria-hidden style={{ marginLeft: 4, color: "var(--cinnabar)" }}>*</span>}
                  </button>
                );
              })}
            </div>
            <label className="block font-body text-xs uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>
              {isFr ? "Titre" : "Title"} ({activeLocale.toUpperCase()})
            </label>
            <input
              data-testid={`push-title-${activeLocale}`}
              className="w-full px-3 py-2 border font-body mb-4"
              style={{ borderColor: "var(--border)" }}
              maxLength={120}
              value={active?.title || ""}
              onChange={(e) => setPayloads((p) => ({ ...p, [activeLocale]: { ...p[activeLocale], title: e.target.value } }))}
              placeholder={isFr ? "Ex. : Fêtes 2027 — Ouverture des inscriptions" : "e.g. Fêtes 2027 — Registration open"}
            />
            <label className="block font-body text-xs uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>
              {isFr ? "Corps du message" : "Body"} ({activeLocale.toUpperCase()})
            </label>
            <textarea
              data-testid={`push-body-${activeLocale}`}
              className="w-full px-3 py-2 border font-body"
              style={{ borderColor: "var(--border)", minHeight: 100 }}
              maxLength={400}
              value={active?.body || ""}
              onChange={(e) => setPayloads((p) => ({ ...p, [activeLocale]: { ...p[activeLocale], body: e.target.value } }))}
              placeholder={
                isFr
                  ? "Écrivez un message court, contemplatif, non commercial."
                  : "Write a short, contemplative, non-commercial message."
              }
            />
            <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
              {(active?.body || "").length}/400
            </div>
          </div>

          {/* Deep-link + Targeting */}
          <div className="bg-white border p-4 space-y-4" style={{ borderColor: "var(--border)" }}>
            <div>
              <label className="block font-body text-xs uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>
                {isFr ? "Lien profond (optionnel)" : "Deep link (optional)"}
              </label>
              <input
                data-testid="push-deeplink"
                className="w-full px-3 py-2 border font-body"
                style={{ borderColor: "var(--border)" }}
                value={deepLinkPath}
                onChange={(e) => setDeepLinkPath(e.target.value)}
                placeholder="/evenements/{id} ou /presence/pre-inscription"
              />
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                {isFr
                  ? "Chemin ouvert automatiquement quand l'utilisateur tape sur la notification."
                  : "Route opened when the user taps the notification."}
              </p>
            </div>

            <div>
              <label className="block font-body text-xs uppercase tracking-widest mb-2" style={{ color: "var(--muted)" }}>
                {isFr ? "Plateformes" : "Platforms"}
              </label>
              <div className="flex gap-4">
                {["ios", "android"].map((p) => (
                  <label key={p} className="inline-flex items-center gap-2 font-body">
                    <input
                      data-testid={`push-plat-${p}`}
                      type="checkbox"
                      checked={platforms[p]}
                      onChange={() => setPlatforms((v) => ({ ...v, [p]: !v[p] }))}
                    />
                    {p === "ios" ? "iOS" : "Android"}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-body text-xs uppercase tracking-widest mb-2" style={{ color: "var(--muted)" }}>
                {isFr ? "Cible : locales" : "Target locales"}
              </label>
              <div className="flex flex-wrap gap-2">
                {LOCALES.map((l) => {
                  const on = targetLocales.includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      data-testid={`push-loc-${l.code}`}
                      onClick={() =>
                        setTargetLocales((prev) =>
                          prev.includes(l.code) ? prev.filter((x) => x !== l.code) : [...prev, l.code]
                        )
                      }
                      className="px-3 py-1 text-xs font-body border"
                      style={{
                        borderColor: on ? "var(--cinnabar)" : "var(--border)",
                        background: on ? "var(--cinnabar)" : "transparent",
                        color: on ? "#fff" : "var(--muted)",
                      }}
                    >
                      {l.code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                {targetLocales.length === 0
                  ? isFr
                    ? "Aucune locale sélectionnée = envoi à toutes."
                    : "No locale selected = broadcast to all."
                  : `${targetLocales.length} ${isFr ? "locales ciblées" : "locales targeted"}`}
              </p>
            </div>

            <div>
              <label className="block font-body text-xs uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>
                {isFr ? "Test uniquement : ids d'appareils" : "Test-only device IDs"}
              </label>
              <input
                data-testid="push-testids"
                className="w-full px-3 py-2 border font-body"
                style={{ borderColor: "var(--border)" }}
                value={testIds}
                onChange={(e) => setTestIds(e.target.value)}
                placeholder="uuid1, uuid2, uuid3"
              />
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                {isFr
                  ? "Sépare par virgule. Si renseigné, seuls ces appareils reçoivent le message (mode QA)."
                  : "Comma-separated. If set, only these devices receive the message (QA mode)."}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="push-dry-run"
              onClick={runDryRun}
              disabled={!canSubmit}
              className="btn btn-outline"
            >
              {isFr ? "Aperçu audience" : "Preview audience"}
            </button>
            <button
              type="button"
              data-testid="push-send"
              onClick={() => setShowConfirm(true)}
              disabled={!canSubmit || sending}
              className="btn btn-primary"
            >
              {sending ? "…" : isFr ? "Envoyer" : "Send"}
            </button>
            {msg && (
              <p
                data-testid="push-msg"
                className="font-body text-sm self-center"
                style={{ color: msg.startsWith("✓") ? "var(--moss, #4a6b3a)" : "var(--cinnabar)" }}
              >
                {msg}
              </p>
            )}
          </div>

          {dryResult && dryResult.dry_run && (
            <div className="border p-3 bg-white text-sm font-body" style={{ borderColor: "var(--gold)" }} data-testid="push-dry-result">
              <div>
                <strong>{isFr ? "Audience potentielle : " : "Would reach: "}</strong>
                {dryResult.would_target}
              </div>
              {dryResult.by_platform && (
                <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                  {Object.entries(dryResult.by_platform).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </div>
              )}
              {dryResult.by_locale && Object.keys(dryResult.by_locale).length > 0 && (
                <div className="text-xs" style={{ color: "var(--muted)" }}>
                  {Object.entries(dryResult.by_locale).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* PREVIEW + AUDIT — 1/3 width */}
        <aside className="space-y-6">
          <div className="bg-white border p-4" style={{ borderColor: "var(--border)" }}>
            <div className="font-caps text-xs mb-3" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
              {isFr ? "APERÇU" : "PREVIEW"}
            </div>
            {/* iOS-ish notification card */}
            <div className="border p-3" style={{
              borderColor: "rgba(0,0,0,0.1)", background: "#f2f2f7", borderRadius: 16,
            }}>
              <div className="flex items-start gap-2">
                <div style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: "var(--parchment)",
                  border: "1px solid var(--gold)",
                  color: "var(--cinnabar)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "Cormorant, serif", fontWeight: 700, fontSize: 16,
                }}>P</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px]" style={{ color: "#8e8e93", letterSpacing: "0.05em" }}>
                    PRESENCE · {isFr ? "maintenant" : "now"}
                  </div>
                  <div className="font-body font-semibold text-sm mt-0.5 truncate" style={{ color: "#000" }}>
                    {previewTitle}
                  </div>
                  <div className="font-body text-xs mt-1" style={{ color: "#3c3c43" }}>
                    {previewBody || (isFr ? "(corps vide)" : "(empty body)")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border p-4" style={{ borderColor: "var(--border)" }} data-testid="push-history">
            <div className="font-caps text-xs mb-3" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
              {isFr ? "HISTORIQUE" : "RECENT"}
            </div>
            {broadcasts.length === 0 ? (
              <p className="font-body italic text-sm" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun envoi encore." : "No broadcasts yet."}
              </p>
            ) : (
              <ul className="space-y-3 max-h-96 overflow-y-auto">
                {broadcasts.slice(0, 20).map((b) => (
                  <li key={b.id} className="border-b pb-2 last:border-b-0" style={{ borderColor: "var(--border)" }}>
                    <div className="font-body text-sm truncate" style={{ color: "var(--ink)" }}>
                      {b.payloads?.fr?.title || b.payloads?.en?.title || "(untitled)"}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                      {new Date(b.created_at).toLocaleString(isFr ? "fr-FR" : "en-GB")} ·
                      {" "}{b.status} · {isFr ? "cible" : "target"} {b.target_count || 0}
                      {typeof b.sent === "number" && ` · ${isFr ? "envoyé" : "sent"} ${b.sent}`}
                      {typeof b.failed === "number" && b.failed > 0 && ` · ${isFr ? "échec" : "failed"} ${b.failed}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(30, 26, 21, 0.55)" }}
          data-testid="push-confirm"
        >
          <div className="bg-white border max-w-md w-full p-6" style={{ borderColor: "var(--gold)" }}>
            <h3 className="font-heading text-xl mb-3" style={{ color: "var(--ink)" }}>
              {isFr ? "Confirmer l'envoi" : "Confirm broadcast"}
            </h3>
            <p className="font-body text-sm mb-4" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Cette action envoie une notification à tous les appareils correspondant aux critères. Elle est définitive."
                : "This will send a notification to every matching device. It cannot be undone."}
            </p>
            <div className="border p-3 mb-5 text-sm" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
              <div style={{ fontWeight: 600 }}>{previewTitle}</div>
              <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>{previewBody}</div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="btn btn-ghost"
                data-testid="push-confirm-cancel"
              >
                {isFr ? "Annuler" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={sending}
                className="btn btn-primary"
                data-testid="push-confirm-send"
              >
                {sending ? "…" : isFr ? "Confirmer et envoyer" : "Confirm and send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
